import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState(""); // phone number state
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validatePhone = (phone) => {
    // Must be digits only and at least 10 characters
    return /^\d{10,}$/.test(phone.trim());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (!validatePhone(phone)) {
      setError("Please enter a valid phone number with at least 10 digits.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          email,
          phone,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Registration failed.");
      }

      setSuccess("Registration successful! Redirecting to login...");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
        {/* Header with logo and titles */}
        <div className="flex flex-col py-4 px-1">
          <div className="flex items-center gap-2 mb-6 self-start">
            <img
              src="/logo.png"
              alt="Logo"
              className="h-10 w-10 p-[2px] rounded"
            />
            <div className="flex flex-col leading-tight">
              <span className="text-black text-xs font-normal whitespace-nowrap">
                Lotus
              </span>
              <span className="text-black text-lg font-semibold whitespace-nowrap">
                WheelsERP
              </span>
            </div>
          </div>
        </div>

        {/* Main form title */}
        <h2 className="text-3xl font-bold text-left mb-8 text-gray-800">
          Create Your Account
        </h2>

        {/* Error / Success messages */}
        {error && (
          <div className="mb-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {success}
          </div>
        )}

        {/* Registration form */}
        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          <label className="block">
            <span className="text-gray-700 font-semibold mb-1 block">
              Full Name
            </span>
            <input
              type="text"
              placeholder="Enter full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={loading}
              className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Full Name"
            />
          </label>

          <label className="block">
            <span className="text-gray-700 font-semibold mb-1 block">
              Email Address
            </span>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Email Address"
              autoComplete="email"
            />
          </label>

          <label className="block">
            <span className="text-gray-700 font-semibold mb-1 block">
              Mobile
            </span>
            <input
              type="tel"
              placeholder="10 digit mobile number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              disabled={loading}
              pattern="\d{10,}"
              title="Mobile number must be at least 10 digits"
              aria-label="Mobile"
              className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </label>

          <label className="block">
            <span className="text-gray-700 font-semibold mb-1 block">
              Password
            </span>
            <input
              type="password"
              placeholder="Create password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              minLength={6}
              className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Password"
            />
          </label>

          <label className="block">
            <span className="text-gray-700 font-semibold mb-1 block">
              Confirm Password
            </span>
            <input
              type="password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              minLength={6}
              className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Confirm Password"
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 text-white rounded font-semibold transition ${
              loading
                ? "bg-indigo-400 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700"
            }`}
            aria-busy={loading}
          >
            {loading ? "Registering..." : "Register"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
